import { combineReducers, compose, Reducer } from 'redux';
import { reducer as chatReducer, MODULE_NAME as chatKey } from '@mews-commander/chat';
import { mergeFilteredPersistedState } from '../enhancers/localStorageEnhancer';

import { coreReducers } from './coreReducers';

import { reducer as alertsReducer, MODULE_NAME as alertsKey } from './alerts';
import { reducer as appReducer, MODULE_NAME as appKey } from './app';
import { reducer as availabilityBlockReducer, MODULE_NAME as availabilityBlockKey } from './availabilityBlock';
import { reducer as customerProfileReducer, MODULE_NAME as customerProfileKey } from './customerProfile';
import { reducer as creditLimitReducer, MODULE_NAME as creditLimitKey } from './creditLimit';
import { reducer as customerInternalsReducer, MODULE_NAME as customerInternalsKey } from './customerInternals';
import { reducer as customerDetailReducer, MODULE_NAME as customerDetailKey } from './customerDetail';
import { reducer as exchangeReducer, MODULE_NAME as exchangeKey } from './exchange';
import { reducer as entityManagementReducer, MODULE_NAME as entityManagementKey } from './entityManagement';
import { reducer as accountNotesReducer, MODULE_NAME as accountNotesKey } from './accountNotes';
import {
    reducer as timelineReducer,
    MODULE_NAME as timelineKey,
    viewReducer as timelineViewReducer,
    TIMELINE_VIEW_NAME as timelineViewKey,
} from './timeline';
import { reducer as tasksReducer, MODULE_NAME as tasksKey } from './tasks';
import { reducer as payoutsReducer, MODULE_NAME as payoutsKey } from './payout';
import { reducer as notificationReducer, MODULE_NAME as notificationKey } from './notifications';
import { reducer as messagesReducer, MODULE_NAME as messagesKey } from './messages';
import { reducer as occupancyReducer, MODULE_NAME as occupancyKey } from './occupancy';
import { reducer as searchReducer, MODULE_NAME as searchKey } from './search';
import { reducer as reservationOverviewReducer, MODULE_NAME as reservationOverviewKey } from './reservationOverview';
import { reducer as scannerReducer, MODULE_NAME as scannerKey } from './identityDocumentScanner';
import { reducer as dashboardReducer, MODULE_NAME as dashboardKey } from './dashboard';
import { reducer as simpleDetailReducer, MODULE_NAME as simpleDetailKey } from './simpleDetail';
import { reducer as reservationModuleReducer, MODULE_NAME as reservationModuleKey } from './reservationModule';
import { reducer as powerBiReducer, MODULE_NAME as powerBiKey } from './powerBi';
import { reducer as enterpriseBuilderReducer, MODULE_NAME as enterpriseBuilderKey } from './enterpriseBuilder';
import { reducer as paginationReducer, MODULE_NAME as paginationKey } from './pagination';
import { reducer as resourcesReducer, MODULE_NAME as resourcesKey } from './resources';
import { reducer as rateManagementReducer, MODULE_NAME as rateManagementKey } from './rateManagement';
import preferences from './preferences';
import { MODULE_NAME as trackingConsentKey, trackingConsentReducer } from './trackingConsent';
import { reducer as productsReducer, MODULE_NAME as productsKey } from './products';
import { reducer as availabilityReducer, MODULE_NAME as availabilityKey } from './availability';
import {
    reducer as availabilityBlockEntityFormReducer,
    MODULE_NAME as availabilityBlockEntityFormKey,
} from './availabilityBlockEntityForm';
import { reducer as dialogReducer, MODULE_NAME as dialogKey } from './dialog';
import {
    reducer as enterpriseRateManagementReducer,
    MODULE_NAME as enterpriseRateManagementKey,
} from './enterpriseRateManagement';
import { billingKey, billingModuleReducer } from './billing';
import { rebateKey, rebateReducer } from './billing/rebate';
import { appSidebarKey, appSidebarReducer } from './appSidebar';
import { enterpriseProfileKey, enterpriseProfileReducer } from './enterpriseProfile';
import { routingRulesKey, routingRulesReducer } from './routingRules';
import { companyDetailReducer, companyDetailReducerKey } from './companyDetail';
import { productServiceOrderKey, productServiceOrderReducer } from './productServiceOrder';
import { restrictionsModuleName, restrictionsReducer } from './restrictions';

const accountUnawareReducers = {
    [appKey]: appReducer,
    [appSidebarKey]: appSidebarReducer,
    [enterpriseProfileKey]: enterpriseProfileReducer,
    [notificationKey]: notificationReducer,
    [preferences.moduleName]: preferences.reducer,
    [trackingConsentKey]: trackingConsentReducer,
    [powerBiKey]: powerBiReducer,
};

const accountAwareReducers = {
    [accountNotesKey]: accountNotesReducer,
    [alertsKey]: alertsReducer,
    [availabilityBlockKey]: availabilityBlockReducer,
    [availabilityBlockEntityFormKey]: availabilityBlockEntityFormReducer,
    [customerProfileKey]: customerProfileReducer,
    [creditLimitKey]: creditLimitReducer,
    [customerDetailKey]: customerDetailReducer,
    [customerInternalsKey]: customerInternalsReducer,
    [entityManagementKey]: entityManagementReducer,
    [exchangeKey]: exchangeReducer,
    [timelineKey]: timelineReducer,
    [timelineViewKey]: timelineViewReducer,
    [messagesKey]: messagesReducer,
    [occupancyKey]: occupancyReducer,
    [searchKey]: searchReducer,
    [reservationOverviewKey]: reservationOverviewReducer,
    [chatKey]: chatReducer,
    [scannerKey]: scannerReducer,
    [dashboardKey]: dashboardReducer,
    [simpleDetailKey]: simpleDetailReducer,
    [tasksKey]: tasksReducer,
    [reservationModuleKey]: reservationModuleReducer,
    [enterpriseBuilderKey]: enterpriseBuilderReducer,
    [paginationKey]: paginationReducer,
    [resourcesKey]: resourcesReducer,
    [rateManagementKey]: rateManagementReducer,
    [productsKey]: productsReducer,
    [availabilityKey]: availabilityReducer,
    [dialogKey]: dialogReducer,
    [enterpriseRateManagementKey]: enterpriseRateManagementReducer,
    [billingKey]: billingModuleReducer,
    [rebateKey]: rebateReducer,
    [payoutsKey]: payoutsReducer,
    [routingRulesKey]: routingRulesReducer,
    [companyDetailReducerKey]: companyDetailReducer,
    [productServiceOrderKey]: productServiceOrderReducer,
    [restrictionsModuleName]: restrictionsReducer,
};

export const combineRootAndAsyncReducers = (asyncReducers: $TSFixMe) =>
    combineReducers({
        ...accountUnawareReducers,
        ...accountAwareReducers,
        ...coreReducers,
        ...asyncReducers,
    });

export const reducers = compose(mergeFilteredPersistedState, combineRootAndAsyncReducers)({}) as Reducer;
